// ─────────────────────────────────────────────────────────────────────────────
// BizPlus parity — composing a user's access payload  (Phase 0.2, wiring)
//
// `PATCH /api/admin/users/:id` REPLACES the permissions array wholesale, so
// whatever this builds is the user's entire access. Composing it partially —
// sending only the sections while forgetting the modules, say — silently
// revokes everything omitted. That is why this lives in one pure, tested
// function rather than being assembled inline in a component.
//
// Shape (must agree with sanitizeWorkspacePermissions on the server):
//   workspace.<module>              one per enabled workspace module
//   workspace.sections.configured   present ONLY when restricting to screens
//   workspace.section.<slug>        one per granted screen
//
// The sections marker is the switch between two meanings:
//   marker ABSENT  → unrestricted; the user sees every screen in their modules
//   marker PRESENT → allowlist; the user sees exactly the listed screens
// So an empty allowlist WITH the marker is a real, different state from no
// restriction at all, and must not collapse into it.
// ─────────────────────────────────────────────────────────────────────────────

import {
  permissionsForWorkspaceAccess,
  type WorkspaceAccessKey,
} from '@/shared/permissions/workspaceAccess';
import {
  WORKSPACE_SECTIONS_PROFILE,
  sectionPermission,
} from '@/shared/permissions/moduleSections';

export interface AccessSelection {
  /** Workspace modules the user holds. */
  modules: readonly WorkspaceAccessKey[];
  /** When false, the user sees every screen in those modules. */
  restrictSections: boolean;
  /** Screen paths allowed when `restrictSections` is true. */
  sections: readonly string[];
}

/**
 * Build the complete permissions array to send.
 *
 * The server prepends `workspace.access.configured` itself when saving with
 * `configured: true`, so it is deliberately not sent here.
 */
export function buildAccessPayload({
  modules,
  restrictSections,
  sections,
}: AccessSelection): string[] {
  const payload = [...permissionsForWorkspaceAccess(modules)];

  if (restrictSections) {
    payload.push(WORKSPACE_SECTIONS_PROFILE);
    // De-duplicate: the same screen can be reached from two modules' lists.
    for (const path of [...new Set(sections)]) {
      payload.push(sectionPermission(path));
    }
  }

  return payload;
}
